import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";
import "daterangepicker";
import { masterDataAPI, territoryMasterAPI, userAPI } from "../services/api";
import { adminDashboardAPI } from "../services/adminDashboardApi";
import {
  getDashboardDateRangePresets,
  getDashboardFinancialYearRange,
} from "../utils/dashboardDateRangePicker";
import {
  parseApiEnvelope,
  extractZonesFromResponse,
  extractRegionsFromResponse,
  normalizeZoneRow,
  normalizeRegionRow,
  normalizeTerritoryRow,
  mapZoneToOption,
  mapExportZonesToOptions,
  mapRegionToOption,
  mapTerritoryToOption,
  formatZoneOptionLabel,
  isExportMarketSelected,
  joinGeoFilter,
  resolveRegionGeoCandidates,
  geoFilterArraysEqual,
  resolveGeoAutoSelectionIds,
  expandZoneFilterCodes,
  getCountryCodesForSelectedZones,
  resolveZoneCodeForCountry,
  findZoneOption,
} from "../utils/dashboardGeoFilterUtils";
import {
  isGeoAllSelectionToken,
  USER_DEFAULT_GEO_CODE,
  filterMarketsForDashboardUser,
  filterGeoOptionsForDashboardUser,
  filterMasterCountriesForDashboardUser,
  getLoggedInUserFromStorage,
  getUserAssignedMarketCodes,
  getUserDashboardLockedMarket,
  isUserGeoAllScopeForDashboard,
  getUserScopedGeoCodesForDashboard,
} from "../utils/userTerritoryScope";
import {
  filterBusinessVerticalOptionsForUser,
  getUserAssignedBusinessVerticalTokens,
} from "../utils/raiseComplaintInvoiceAuth";

const GEO_ALL_OPTION = { id: USER_DEFAULT_GEO_CODE, text: "All" };
const GEO_DROPDOWN_LOAD_MIN_MS = 150;

const isGeoAllOptionRow = (id, text) => {
  if (isGeoAllSelectionToken(id)) return true;
  return String(text ?? "").trim().toLowerCase() === "all";
};

const selectionIncludesGeoAll = (selected, optionList = []) => {
  const ids = Array.isArray(selected) ? selected : [];
  return ids.some((id) => {
    const sid = String(id ?? "").trim();
    if (!sid) return false;
    if (isGeoAllSelectionToken(sid)) return true;
    const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === sid);
    return isGeoAllOptionRow(sid, opt?.text);
  });
};

const isGeoAllFilterValue = (val) =>
  Array.isArray(val) &&
  val.length > 0 &&
  val.every((id) => isGeoAllSelectionToken(id));

export const joinGeoFilterForApi = (val) => {
  if (isGeoAllFilterValue(val)) return null;
  if (Array.isArray(val) && val.some(isGeoAllSelectionToken)) {
    const real = val.filter((id) => !isGeoAllSelectionToken(id));
    return joinGeoFilter(real);
  }
  return joinGeoFilter(val);
};

export const pickDefaultMarket = (options, fallback = "M001") => {
  if (!Array.isArray(options) || !options.length) return fallback;
  const byCode = options.find(
    (o) => String(o.id || "").trim().toUpperCase() === fallback.toUpperCase()
  );
  if (byCode?.id) return byCode.id;
  const byName = options.find(
    (o) => String(o.text || "").trim().toLowerCase() === "domestic"
  );
  if (byName?.id) return byName.id;
  return options[0]?.id || fallback;
};

/**
 * Admin Dashboard filter state + loaders (shared by SR Report page).
 * When scopeToUser is true, market/geo/BV options are limited to the logged-in user's assignment.
 */
export function useAdminDashboardFilters({ defaultMarket = "M001", scopeToUser = false } = {}) {
  const defaultFY = getDashboardFinancialYearRange();
  const filterDateRangePickerRef = useRef(null);
  const zoneOptionsRef = useRef([]);
  const regionOptionsRef = useRef([]);
  const userGeographyRef = useRef(null);
  const prevExportCascadeRef = useRef(null);
  const geoLoadGenRef = useRef({ country: 0, zone: 0, region: 0, territory: 0 });

  const loginUser = useMemo(
    () => (scopeToUser ? getLoggedInUserFromStorage() : null),
    [scopeToUser]
  );
  const assignedMarketCodes = useMemo(
    () => (scopeToUser ? getUserAssignedMarketCodes(loginUser) : []),
    [scopeToUser, loginUser]
  );
  const marketDropdownDisabled = scopeToUser && assignedMarketCodes.length <= 1;

  const withGeoAllOption = useCallback(
    (options, level) => {
      const list = (Array.isArray(options) ? options : []).filter(
        (o) => o && !isGeoAllOptionRow(o.id, o.text)
      );
      if (!scopeToUser || isUserGeoAllScopeForDashboard(loginUser, level)) {
        return [GEO_ALL_OPTION, ...list];
      }
      return list;
    },
    [scopeToUser, loginUser]
  );

  const [filterExpanded, setFilterExpanded] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
    status: [],
    complaintCategory: "",
    bv: [],
    market: defaultMarket,
    country: [],
    zone: [],
    region: [],
    territory: [],
  });

  const [statusOptions, setStatusOptions] = useState([]);
  const [srCategoryOptions, setSrCategoryOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [geoLoading, setGeoLoading] = useState({
    country: false,
    zone: false,
    region: false,
    territory: false,
  });
  const [userGeography, setUserGeography] = useState(null);

  const loadUserGeography = async () => {
    if (!scopeToUser || !loginUser) return;
    const id = loginUser.userIdCode || loginUser.UserIdCode;
    if (!id) return;
    try {
      const res = await userAPI.getGeography(id);
      const payload = res?.data ?? res?.Data ?? res;
      userGeographyRef.current = payload;
      setUserGeography(payload);
    } catch (e) {
      console.error("Error loading user geography for scoped filters:", e);
    }
  };

  useEffect(() => {
    zoneOptionsRef.current = zoneOptions;
  }, [zoneOptions]);

  useEffect(() => {
    regionOptionsRef.current = regionOptions;
  }, [regionOptions]);

  const isExportCascade = useMemo(
    () => isExportMarketSelected(filters.market, marketOptions),
    [filters.market, marketOptions]
  );
  const isExportCascadeRef = useRef(isExportCascade);
  isExportCascadeRef.current = isExportCascade;

  /** Stable zone option ids — label-only refreshes must not reload regions. */
  const zoneOptionIdsKey = useMemo(
    () =>
      (zoneOptions || [])
        .map((o) => String(o?.id ?? "").trim())
        .filter(Boolean)
        .join("|"),
    [zoneOptions]
  );

  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade || !filters.zone?.length) return [];
    if (isGeoAllFilterValue(filters.zone)) return [GEO_ALL_OPTION];
    const codes = getCountryCodesForSelectedZones(filters.zone, zoneOptions);
    if (!codes.size) return countryOptions;
    return countryOptions.filter(
      (c) => codes.has(c.id) || isGeoAllSelectionToken(c.id)
    );
  }, [isExportCascade, filters.zone, zoneOptions, countryOptions]);

  const runGeoDropdownLoad = async (level, task) => {
    geoLoadGenRef.current[level] += 1;
    const generation = geoLoadGenRef.current[level];
    const isCurrent = () => geoLoadGenRef.current[level] === generation;
    setGeoLoading((prev) => ({ ...prev, [level]: true }));
    const startedAt = Date.now();
    try {
      await task(isCurrent);
    } finally {
      const elapsed = Date.now() - startedAt;
      const remain = GEO_DROPDOWN_LOAD_MIN_MS - elapsed;
      if (remain > 0) await new Promise((r) => setTimeout(r, remain));
      if (isCurrent()) {
        setGeoLoading((prev) => ({ ...prev, [level]: false }));
      }
    }
  };

  const loadStatusOptions = async () => {
    try {
      const response = await adminDashboardAPI.getSRStatuses(true);
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        setStatusOptions(
          list.map((s) => ({
            id: s.srStatusCode || s.SRStatusCode || "",
            text: s.name || s.Name || s.srStatusCode || s.SRStatusCode || "",
          }))
        );
      }
    } catch (e) {
      console.error("Error loading status options:", e);
    }
  };

  const loadSrCategoryOptions = async (marketCode) => {
    try {
      const code = marketCode != null ? String(marketCode).trim() : "";
      if (!code || code === "0000") {
        setSrCategoryOptions([]);
        return;
      }
      const response = await masterDataAPI.getSRTypes(code, true);
      const payload = response?.data ?? response?.Data ?? response;
      const list =
        (Array.isArray(payload) ? payload : null) ||
        (Array.isArray(payload?.data) ? payload.data : null) ||
        (Array.isArray(payload?.srTypes) ? payload.srTypes : null) ||
        [];
      const seen = new Set();
      const options = [];
      (Array.isArray(list) ? list : []).forEach((x) => {
        const srTypeCode = String(
          x.srTypeCode ?? x.SRTypeCode ?? x.SRTYPECODE ?? x.value ?? x.Value ?? ""
        ).trim();
        const name = String(x.name ?? x.Name ?? x.NAME ?? x.text ?? x.Text ?? "").trim();
        const listLabel = (name || srTypeCode).trim();
        if (!listLabel) return;
        const dedupeKey = listLabel.toLowerCase();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        options.push({ id: listLabel, text: listLabel, srTypeCode });
      });
      setSrCategoryOptions(options);
    } catch (e) {
      console.error("Error loading SR category options:", e);
      setSrCategoryOptions([]);
    }
  };

  const loadBVOptions = async (marketCode) => {
    try {
      const response = await adminDashboardAPI.getProductVerticalOptions({
        isActive: true,
        marketCode: marketCode || undefined,
        pageNumber: 1,
        pageSize: 500,
      });
      if (response?.success && response.data?.categories) {
        const list = response.data.categories || response.data.Categories || [];
        let options = list.map((c) => ({
          id: c.catgCode || c.CatgCode,
          text: c.catgName || c.CatgName || c.catgCode || c.CatgCode,
        }));
        if (scopeToUser) {
          options = filterBusinessVerticalOptionsForUser(options, loginUser);
        }
        setBvOptions(options);
        if (scopeToUser && options.length === 1) {
          const onlyId = options[0].id;
          setFilters((prev) =>
            prev.bv?.length === 1 && prev.bv[0] === onlyId
              ? prev
              : { ...prev, bv: [onlyId] }
          );
        }
      } else {
        setBvOptions([]);
      }
    } catch (e) {
      console.error("Error loading BV options:", e);
      setBvOptions([]);
    }
  };

  const loadMarketOptions = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const { payload } = parseApiEnvelope(response);
      const markets = payload?.markets || payload?.Markets || payload || [];
      const allOptions = (Array.isArray(markets) ? markets : [])
        .filter((m) => m.isActive !== false && m.IsActive !== false)
        .map((m) => ({
          id: m.marketCode || m.MARKETCODE || m.MarketCode,
          text: m.marketName || m.MARKETNAME || m.MarketName || m.marketCode,
        }))
        .filter((o) => o.id);
      const options = scopeToUser
        ? filterMarketsForDashboardUser(allOptions, loginUser)
        : allOptions;
      setMarketOptions(options);

      // Prefer locked market (e.g. Export-only user → M002). Do not keep the
      // hardcoded defaultMarket (M001) when it is not in the user's options —
      // that caused empty SR Report / SR Detail Dump for Export assignees.
      const lockedMarket = scopeToUser ? getUserDashboardLockedMarket(loginUser) : null;
      let nextMarket = "";
      setFilters((prev) => {
        const prevCode = String(prev.market || "").trim();
        const prevInOptions =
          !!prevCode &&
          options.some(
            (o) =>
              String(o.id || "").trim().toUpperCase() === prevCode.toUpperCase()
          );
        nextMarket =
          lockedMarket ||
          (prevInOptions ? prevCode : "") ||
          pickDefaultMarket(options, defaultMarket);
        if (prev.market === nextMarket) return prev;
        return { ...prev, market: nextMarket };
      });
      if (!nextMarket) nextMarket = lockedMarket || pickDefaultMarket(options, defaultMarket);
      return nextMarket;
    } catch (e) {
      console.error("Error loading Market options:", e);
      return "";
    }
  };

  const loadCountryOptions = async () => {
    await runGeoDropdownLoad("country", async () => {
      try {
        const response = await masterDataAPI.getCountries();
        const { ok, payload } = parseApiEnvelope(response);
        const raw = ok && Array.isArray(payload) ? payload : [];

        if (scopeToUser) {
          const scoped = filterMasterCountriesForDashboardUser(
            raw,
            loginUser,
            userGeographyRef.current
          );
          const options = withGeoAllOption(scoped, "country");
          setCountryOptions(options);
          setFilters((prev) => {
            if (!prev.market) return prev;
            const exportMode = isExportMarketSelected(prev.market, marketOptions);
            if (exportMode) return prev;
            if (isUserGeoAllScopeForDashboard(loginUser, "country")) {
              const allVal = [USER_DEFAULT_GEO_CODE];
              if (geoFilterArraysEqual(prev.country, allVal)) return prev;
              return { ...prev, country: allVal, zone: allVal, region: allVal, territory: allVal };
            }
            const autoIds = resolveGeoAutoSelectionIds({
              scopedOptions: options,
              user: loginUser,
              level: "country",
              isExportCascade: false,
              parentReady: true,
            });
            if (autoIds && !geoFilterArraysEqual(prev.country, autoIds)) {
              return { ...prev, country: autoIds, zone: [], region: [], territory: [] };
            }
            return prev;
          });
          return;
        }

        const byId = new Map();
        raw.forEach((country) => {
          const id = String(country.countryCode ?? country.CountryCode ?? "").trim();
          const text = String(country.countryName ?? country.CountryName ?? "").trim();
          if (!id) return;
          if (isGeoAllOptionRow(id, text)) {
            byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
            return;
          }
          byId.set(id, { id, text: text || id });
        });
        if (!byId.has(USER_DEFAULT_GEO_CODE)) {
          byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
        }
        setCountryOptions([...byId.values()]);
      } catch (e) {
        console.error("Error loading Country options:", e);
        setCountryOptions(scopeToUser ? [] : [GEO_ALL_OPTION]);
      }
    });
  };

  const loadZoneOptions = async (countryCodes) => {
    await runGeoDropdownLoad("zone", async (isCurrent) => {
      try {
        const merged = [];
        const codes = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!codes.length) {
          const res = await territoryMasterAPI.getZones();
          if (!isCurrent()) return;
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) =>
            merged.push(normalizeZoneRow(z, z.countryCode || z.CountryCode))
          );
        } else {
          const zoneResults = await Promise.all(
            codes.map(async (cc) => {
              const res = await territoryMasterAPI.getZones(cc);
              const raw = extractZonesFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
                : [];
              return active.map((z) => normalizeZoneRow(z, cc));
            })
          );
          if (!isCurrent()) return;
          zoneResults.flat().forEach((z) => merged.push(z));
        }
        const activeRows = merged.filter(
          (z) => z.zoneCode && !isGeoAllOptionRow(z.zoneCode, z.zoneName)
        );
        // Use live cascade flag (not a stale closure from when the request started).
        const exportMode = isExportCascadeRef.current;
        let mapped = exportMode
          ? mapExportZonesToOptions(activeRows, countryOptions)
          : (() => {
              const byZone = new Map();
              activeRows.forEach((z) => {
                byZone.set(z.zoneCode, mapZoneToOption(z, countryOptions));
              });
              return [...byZone.values()];
            })();
        if (scopeToUser) {
          mapped = filterGeoOptionsForDashboardUser(
            mapped,
            loginUser,
            "zone",
            userGeographyRef.current
          );
        }
        if (!isCurrent()) return;
        const options = withGeoAllOption(mapped, "zone");
        setZoneOptions(options);
        if (scopeToUser) {
          setFilters((prev) => {
            if (!prev.market) return prev;
            const exportModeAuto = isExportMarketSelected(prev.market, marketOptions);
            if (isUserGeoAllScopeForDashboard(loginUser, "zone")) {
              const allVal = [USER_DEFAULT_GEO_CODE];
              if (geoFilterArraysEqual(prev.zone, allVal)) return prev;
              return { ...prev, zone: allVal, region: allVal, territory: allVal };
            }
            const autoIds = resolveGeoAutoSelectionIds({
              scopedOptions: options,
              user: loginUser,
              level: "zone",
              isExportCascade: exportModeAuto,
              parentReady: exportModeAuto || !!prev.country?.length,
            });
            if (autoIds && !geoFilterArraysEqual(prev.zone, autoIds)) {
              return { ...prev, zone: autoIds, region: [], territory: [] };
            }
            return prev;
          });
        }
      } catch (e) {
        console.error("Error loading Zone options:", e);
        if (!isCurrent()) return;
        setZoneOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadRegionOptions = async (zoneCodes, countryCodes, zonesList, exportMode) => {
    await runGeoDropdownLoad("region", async (isCurrent) => {
      try {
        const zSel = Array.isArray(zoneCodes)
          ? zoneCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        const cSel = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!zSel.length || (exportMode && !cSel.length)) {
          if (!isCurrent()) return;
          regionOptionsRef.current = [GEO_ALL_OPTION];
          setRegionOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionJobs = [];
        for (const zoneOptionId of zSel) {
          const zrow = findZoneOption(zonesList, zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes.map((c) => String(c).trim()).filter(Boolean)
            : zrow?.countryCode
              ? [String(zrow.countryCode).trim()]
              : [];
          // Domestic: prefer selected filter countries; never fan-out grouped-zone countries
          // with a single zoneOptionId (that breaks Region after Export→Domestic races).
          const countriesToQuery = exportMode
            ? cSel.filter(
                (cc) => !linkedCountries.length || linkedCountries.includes(String(cc).trim())
              )
            : cSel.length
              ? cSel.filter(
                  (cc) =>
                    !linkedCountries.length || linkedCountries.includes(String(cc).trim())
                )
              : linkedCountries.length
                ? linkedCountries
                : [];
          for (const cc of countriesToQuery) {
            if (!cc || isGeoAllSelectionToken(cc)) continue;
            const zoneCode =
              resolveZoneCodeForCountry(zoneOptionId, cc, zonesList) || zoneOptionId;
            if (!zoneCode) continue;
            regionJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getRegions(cc, zoneCode);
                const raw = extractRegionsFromResponse(res);
                const active = Array.isArray(raw)
                  ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                  : [];
                return active;
              })()
            );
          }
        }
        const regionResults = await Promise.all(regionJobs);
        if (!isCurrent()) return;
        const merged = [];
        regionResults.forEach((active) => {
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        });
        const byReg = new Map();
        merged.forEach((r) => {
          if (!r.regionCode || isGeoAllOptionRow(r.regionCode, r.regionName)) return;
          byReg.set(r.regionCode, mapRegionToOption(r));
        });
        let mapped = [...byReg.values()];
        if (scopeToUser) {
          mapped = filterGeoOptionsForDashboardUser(
            mapped,
            loginUser,
            "region",
            userGeographyRef.current
          );
        }
        if (!isCurrent()) return;
        const options = withGeoAllOption(mapped, "region");
        regionOptionsRef.current = options;
        setRegionOptions(options);
        if (scopeToUser) {
          setFilters((prev) => {
            if (!prev.market) return prev;
            const exportModeAuto = isExportMarketSelected(prev.market, marketOptions);
            if (isUserGeoAllScopeForDashboard(loginUser, "region")) {
              const allVal = [USER_DEFAULT_GEO_CODE];
              if (geoFilterArraysEqual(prev.region, allVal)) return prev;
              return { ...prev, region: allVal, territory: allVal };
            }
            const parentReady = exportModeAuto
              ? !!prev.zone?.length && !!prev.country?.length
              : !!prev.zone?.length;
            const autoIds = resolveGeoAutoSelectionIds({
              scopedOptions: options,
              user: loginUser,
              level: "region",
              isExportCascade: exportModeAuto,
              parentReady,
            });
            if (autoIds && !geoFilterArraysEqual(prev.region, autoIds)) {
              return { ...prev, region: autoIds, territory: [] };
            }
            return prev;
          });
        }
      } catch (e) {
        console.error("Error loading Region options:", e);
        if (!isCurrent()) return;
        regionOptionsRef.current = [GEO_ALL_OPTION];
        setRegionOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadTerritoryOptions = async (
    regionCodes,
    zonesList,
    regionsList,
    filterZoneCodes,
    filterCountryCodes
  ) => {
    await runGeoDropdownLoad("territory", async (isCurrent) => {
      try {
        const rSel = Array.isArray(regionCodes)
          ? regionCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!rSel.length) {
          if (!isCurrent()) return;
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionsListResolved =
          (Array.isArray(regionsList) && regionsList.length
            ? regionsList
            : regionOptionsRef.current) || [];
        const zonesListResolved =
          (Array.isArray(zonesList) && zonesList.length
            ? zonesList
            : zoneOptionsRef.current) || [];

        const territoryJobs = [];
        for (const rc of rSel) {
          const row = regionsListResolved.find((x) => x.id === rc);
          const pairs = resolveRegionGeoCandidates(
            rc,
            row,
            regionsListResolved,
            zonesListResolved,
            filterZoneCodes,
            filterCountryCodes
          );
          if (!pairs.length) continue;
          for (const { cc, zc } of pairs) {
            if (isGeoAllSelectionToken(cc) || isGeoAllSelectionToken(zc)) continue;
            territoryJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getPaged({
                  countryCode: cc,
                  zoneCode: zc,
                  regionCode: rc,
                  status: "active",
                  pageNumber: 1,
                  pageSize: 5000,
                });
                const { ok, payload } = parseApiEnvelope(res);
                const items =
                  ok && payload
                    ? payload.territories || payload.Territories || []
                    : [];
                return Array.isArray(items)
                  ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
                  : [];
              })()
            );
          }
        }
        const territoryResults = await Promise.all(territoryJobs);
        if (!isCurrent()) return;
        const merged = [];
        territoryResults.forEach((active) => {
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        });
        const byT = new Map();
        merged.forEach((t) => {
          if (!t.territoryCode || isGeoAllOptionRow(t.territoryCode, t.territoryName)) {
            return;
          }
          byT.set(t.territoryCode, mapTerritoryToOption(t));
        });
        let mapped = [...byT.values()];
        if (scopeToUser) {
          mapped = filterGeoOptionsForDashboardUser(
            mapped,
            loginUser,
            "territory",
            userGeographyRef.current
          );
        }
        if (!isCurrent()) return;
        const options = withGeoAllOption(mapped, "territory");
        setTerritoryOptions(options);
        if (scopeToUser) {
          setFilters((prev) => {
            if (!prev.market) return prev;
            if (isUserGeoAllScopeForDashboard(loginUser, "territory")) {
              const allVal = [USER_DEFAULT_GEO_CODE];
              if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
              return { ...prev, territory: allVal };
            }
            const autoIds = resolveGeoAutoSelectionIds({
              scopedOptions: options,
              user: loginUser,
              level: "territory",
              isExportCascade: isExportMarketSelected(prev.market, marketOptions),
              parentReady: !!prev.region?.length,
            });
            if (autoIds && !geoFilterArraysEqual(prev.territory, autoIds)) {
              return { ...prev, territory: autoIds };
            }
            return prev;
          });
        }
      } catch (e) {
        console.error("Error loading Territory options:", e);
        if (!isCurrent()) return;
        setTerritoryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const handleGeoFilterChange = (field, value, optionList = []) => {
    const exportCascade = isExportMarketSelected(filters.market, marketOptions);
    const raw = Array.isArray(value)
      ? value.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];

    const realOnly = raw.filter((id) => {
      const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === id);
      return !isGeoAllOptionRow(id, opt?.text);
    });
    const pickedAll = selectionIncludesGeoAll(raw, optionList);
    const prevWasAll = isGeoAllFilterValue(filters[field]);

    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      const allVal = [USER_DEFAULT_GEO_CODE];
      if (field === "country") {
        setZoneOptions([GEO_ALL_OPTION]);
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({
          ...prev,
          country: allVal,
          zone: allVal,
          region: allVal,
          territory: allVal,
        }));
        return;
      }
      if (field === "zone") {
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        if (exportCascade) {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            country: allVal,
            region: allVal,
            territory: allVal,
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            region: allVal,
            territory: allVal,
          }));
        }
        return;
      }
      if (field === "region") {
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({ ...prev, region: allVal, territory: allVal }));
        return;
      }
      if (field === "territory") {
        setFilters((prev) => ({ ...prev, territory: allVal }));
        return;
      }
    }

    const selected = realOnly;
    setFilters((prev) => {
      if (field === "country") {
        if (exportCascade) {
          return { ...prev, country: selected, region: [], territory: [] };
        }
        return { ...prev, country: selected, zone: [], region: [], territory: [] };
      }
      if (field === "zone") {
        if (exportCascade) {
          return { ...prev, zone: selected, country: [], region: [], territory: [] };
        }
        return { ...prev, zone: selected, region: [], territory: [] };
      }
      if (field === "region") {
        return { ...prev, region: selected, territory: [] };
      }
      if (field === "territory") {
        return { ...prev, territory: selected };
      }
      return prev;
    });
  };

  const buildFilterParams = useCallback(
    (override = {}) => {
      const f = { ...filters, ...override };
      const params = {};
      if (f.dateRangeStart) params.fromDate = f.dateRangeStart;
      if (f.dateRangeEnd) params.toDate = f.dateRangeEnd;
      if (f.market) params.marketCode = f.market;
      if (Array.isArray(f.status) && f.status.length) {
        params.statusCode = f.status.filter(Boolean).join(",");
      }

      const realOptionIds = (optionList) =>
        (Array.isArray(optionList) ? optionList : [])
          .map((o) => String(o?.id ?? "").trim())
          .filter((id) => id && !isGeoAllSelectionToken(id) && !isGeoAllOptionRow(id, ""));

      /**
       * For user-scoped reports: "All" / empty must still send the user's assigned
       * codes. Otherwise the API returns unscoped (all) data.
       */
      const resolveGeoCsv = (selected, level, optionList) => {
        const fromUi = joinGeoFilterForApi(selected);
        if (fromUi) return fromUi;
        if (!scopeToUser) return null;
        // User assigned All (0000): do not expand to every master code (DOS Admin hang).
        if (isUserGeoAllScopeForDashboard(loginUser, level)) return null;

        const fromOptions = realOptionIds(optionList);
        if (fromOptions.length) return fromOptions.join(",");

        const fromUser = getUserScopedGeoCodesForDashboard(
          loginUser,
          level,
          userGeographyRef.current
        );
        if (fromUser === null) return null;
        if (Array.isArray(fromUser) && fromUser.length) {
          return fromUser.filter((c) => c && !isGeoAllSelectionToken(c)).join(",") || null;
        }
        return null;
      };

      let bvCsv = null;
      if (Array.isArray(f.bv) && f.bv.length) {
        const parts = [];
        f.bv.filter(Boolean).forEach((id) => {
          const sid = String(id).trim();
          if (!sid) return;
          parts.push(sid);
          const opt = (bvOptions || []).find((o) => String(o?.id ?? "").trim() === sid);
          const label = String(opt?.text ?? "").trim();
          if (label && label !== sid) parts.push(label);
          const baseName = label
            .replace(/\s*\(\s*Domestic\s*\)\s*$/i, "")
            .replace(/\s*\(\s*Export\s*\)\s*$/i, "")
            .trim();
          if (baseName && baseName !== sid && baseName !== label) parts.push(baseName);
        });
        bvCsv = [...new Set(parts)].join(",") || null;
      } else if (scopeToUser) {
        const fromOptions = realOptionIds(bvOptions);
        if (fromOptions.length) {
          bvCsv = fromOptions.join(",");
        } else {
          const assigned = getUserAssignedBusinessVerticalTokens(loginUser);
          if (assigned.codes.length) bvCsv = assigned.codes.join(",");
          else if (assigned.names.length) bvCsv = assigned.names.join(",");
        }
      }
      if (bvCsv) params.productVerticalCode = bvCsv;

      const countryList = isExportCascade ? exportCountryOptions : countryOptions;
      const countryCsv = resolveGeoCsv(f.country, "country", countryList);
      let zoneCsv = resolveGeoCsv(f.zone, "zone", zoneOptions);
      if (zoneCsv && isExportCascade) {
        const selectedIds = String(zoneCsv)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const expanded = expandZoneFilterCodes(selectedIds, zoneOptions);
        zoneCsv = expanded.length ? expanded.join(",") : zoneCsv;
      }
      const regionCsv = resolveGeoCsv(f.region, "region", regionOptions);
      const territoryCsv = resolveGeoCsv(f.territory, "territory", territoryOptions);
      if (countryCsv) params.countryCode = countryCsv;
      if (zoneCsv) params.zoneCode = zoneCsv;
      if (regionCsv) params.regionCode = regionCsv;
      if (territoryCsv) params.territoryCode = territoryCsv;
      if (f.complaintCategory) params.complaintCategory = f.complaintCategory;
      return params;
    },
    [
      filters,
      scopeToUser,
      loginUser,
      bvOptions,
      isExportCascade,
      exportCountryOptions,
      countryOptions,
      zoneOptions,
      regionOptions,
      territoryOptions,
    ]
  );

  const getResetState = useCallback(() => {
    const fy = getDashboardFinancialYearRange();
    const lockedMarket = scopeToUser ? getUserDashboardLockedMarket(loginUser) : null;
    const market = lockedMarket || pickDefaultMarket(marketOptions, defaultMarket);
    return {
      filters: {
        dateRange: fy.dateRange,
        dateRangeStart: fy.dateRangeStart,
        dateRangeEnd: fy.dateRangeEnd,
        status: [],
        complaintCategory: "",
        bv: [],
        market,
        country: [],
        zone: [],
        region: [],
        territory: [],
      },
      params: {
        dateRange: fy.dateRange,
        dateRangeStart: fy.dateRangeStart,
        dateRangeEnd: fy.dateRangeEnd,
        status: [],
        bv: [],
        country: [],
        zone: [],
        region: [],
        territory: [],
        complaintCategory: "",
        market,
      },
    };
  }, [marketOptions, defaultMarket, scopeToUser, loginUser]);

  const handleResetFilters = useCallback(() => {
    const { filters: nextFilters, params } = getResetState();
    setFilters(nextFilters);
    if (filterDateRangePickerRef.current) {
      $(filterDateRangePickerRef.current).val(nextFilters.dateRange);
    }
    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    return params;
  }, [getResetState]);

  useEffect(() => {
    const boot = async () => {
      if (scopeToUser) {
        await loadUserGeography();
      }
      loadStatusOptions();
      const market = await loadMarketOptions();
      if (market) {
        loadCountryOptions();
        if (isExportMarketSelected(market, marketOptions)) {
          loadZoneOptions();
        }
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!scopeToUser || !filters.market) return;
    if (isExportCascade) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "zone")) return;
    if (!filters.country?.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.zone, allVal)) return prev;
      return { ...prev, zone: allVal, region: allVal, territory: allVal };
    });
  }, [scopeToUser, loginUser, isExportCascade, filters.country, filters.market]);

  useEffect(() => {
    if (!scopeToUser || !filters.market) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "country")) return;
    const parentOk = !isExportCascade || filters.zone?.length;
    if (!parentOk) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.country, allVal)) return prev;
      if (isExportCascade) {
        return { ...prev, country: allVal, region: allVal, territory: allVal };
      }
      return { ...prev, country: allVal, zone: allVal, region: allVal, territory: allVal };
    });
  }, [scopeToUser, loginUser, isExportCascade, filters.zone, filters.market]);

  useEffect(() => {
    if (!scopeToUser || !filters.market) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "region")) return;
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (!zOk || !cOk) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.region, allVal)) return prev;
      return { ...prev, region: allVal, territory: allVal };
    });
  }, [
    scopeToUser,
    loginUser,
    isExportCascade,
    filters.zone,
    filters.country,
    filters.market,
  ]);

  useEffect(() => {
    if (!scopeToUser || !filters.market) return;
    if (!isUserGeoAllScopeForDashboard(loginUser, "territory")) return;
    if (!filters.region?.length) return;
    const allVal = [USER_DEFAULT_GEO_CODE];
    setFilters((prev) => {
      if (geoFilterArraysEqual(prev.territory, allVal)) return prev;
      return { ...prev, territory: allVal };
    });
  }, [scopeToUser, loginUser, filters.region, filters.market]);

  useEffect(() => {
    if (!filters.market) {
      setBvOptions([]);
      setSrCategoryOptions([]);
      return;
    }
    loadBVOptions(filters.market);
    loadSrCategoryOptions(filters.market);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market]);

  useEffect(() => {
    if (!isExportCascade) return;
    loadZoneOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  useEffect(() => {
    if (isExportCascade) return;
    if (isGeoAllFilterValue(filters.country)) {
      if (scopeToUser) {
        // Keep real scoped zone options available (for API expand + UI), not only "All".
        // All-geo users (e.g. DOS Admin 0000): do not fan-out getZones for every country.
        if (isUserGeoAllScopeForDashboard(loginUser, "country")) {
          loadZoneOptions();
          return;
        }
        const assignedCountries = getUserScopedGeoCodesForDashboard(
          loginUser,
          "country",
          userGeographyRef.current
        );
        const fromCountries = (Array.isArray(countryOptions) ? countryOptions : [])
          .map((o) => String(o?.id ?? "").trim())
          .filter((id) => id && !isGeoAllSelectionToken(id));
        const countryCodes =
          Array.isArray(assignedCountries) && assignedCountries.length
            ? assignedCountries.filter((c) => c && !isGeoAllSelectionToken(c))
            : fromCountries;
        if (countryCodes.length) {
          loadZoneOptions(countryCodes);
        } else {
          loadZoneOptions();
        }
        return;
      }
      setZoneOptions([GEO_ALL_OPTION]);
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (filters.country?.length) {
      setZoneOptions([]);
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: [], region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.country, scopeToUser]);

  useEffect(() => {
    if (prevExportCascadeRef.current === null) {
      prevExportCascadeRef.current = isExportCascade;
      return;
    }
    if (prevExportCascadeRef.current === isExportCascade) return;
    prevExportCascadeRef.current = isExportCascade;
    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    setFilters((prev) => ({
      ...prev,
      country: [],
      zone: [],
      region: [],
      territory: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  useEffect(() => {
    if (!zoneOptions.length || !countryOptions.length) return;
    if (isGeoAllFilterValue(filters.zone)) return;
    setZoneOptions((prev) =>
      prev.map((z) => {
        if (isGeoAllSelectionToken(z.id) || isGeoAllOptionRow(z.id, z.text)) {
          return z;
        }
        return {
          ...z,
          text: formatZoneOptionLabel(z, countryOptions, {
            includeCountry: !isExportCascade,
          }),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, isExportCascade]);

  useEffect(() => {
    if (isGeoAllFilterValue(filters.zone) || isGeoAllFilterValue(filters.country)) {
      if (scopeToUser) {
        if (
          isUserGeoAllScopeForDashboard(loginUser, "zone") ||
          isUserGeoAllScopeForDashboard(loginUser, "country")
        ) {
          setRegionOptions([GEO_ALL_OPTION]);
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }
        const assignedZones = getUserScopedGeoCodesForDashboard(
          loginUser,
          "zone",
          userGeographyRef.current
        );
        const assignedCountries = getUserScopedGeoCodesForDashboard(
          loginUser,
          "country",
          userGeographyRef.current
        );
        const zoneCodes = isGeoAllFilterValue(filters.zone)
          ? Array.isArray(assignedZones) && assignedZones.length
            ? assignedZones.filter((id) => id && !isGeoAllSelectionToken(id))
            : zoneOptions.map((o) => o.id).filter((id) => id && !isGeoAllSelectionToken(id))
          : (filters.zone || []).filter((id) => !isGeoAllSelectionToken(id));
        const countryCodes = isGeoAllFilterValue(filters.country)
          ? Array.isArray(assignedCountries) && assignedCountries.length
            ? assignedCountries.filter((id) => id && !isGeoAllSelectionToken(id))
            : countryOptions.map((o) => o.id).filter((id) => id && !isGeoAllSelectionToken(id))
          : (filters.country || []).filter((id) => !isGeoAllSelectionToken(id));
        if (zoneCodes?.length && (!isExportCascade || countryCodes?.length)) {
          loadRegionOptions(
            zoneCodes,
            countryCodes,
            zoneOptionsRef.current.length ? zoneOptionsRef.current : zoneOptions,
            isExportCascade
          );
        }
        return;
      }
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (zOk && cOk) {
      setRegionOptions([]);
      regionOptionsRef.current = [];
      loadRegionOptions(
        filters.zone,
        filters.country,
        zoneOptionsRef.current.length ? zoneOptionsRef.current : zoneOptions,
        isExportCascade
      );
    } else {
      setRegionOptions([]);
      regionOptionsRef.current = [];
      setFilters((prev) => ({ ...prev, region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country, isExportCascade, zoneOptionIdsKey, scopeToUser]);

  useEffect(() => {
    if (
      isGeoAllFilterValue(filters.region) ||
      isGeoAllFilterValue(filters.zone) ||
      isGeoAllFilterValue(filters.country)
    ) {
      if (scopeToUser) {
        if (
          isUserGeoAllScopeForDashboard(loginUser, "region") ||
          isUserGeoAllScopeForDashboard(loginUser, "zone") ||
          isUserGeoAllScopeForDashboard(loginUser, "country")
        ) {
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }
        const assignedRegions = getUserScopedGeoCodesForDashboard(
          loginUser,
          "region",
          userGeographyRef.current
        );
        const regionCodes = isGeoAllFilterValue(filters.region)
          ? Array.isArray(assignedRegions) && assignedRegions.length
            ? assignedRegions.filter((id) => id && !isGeoAllSelectionToken(id))
            : regionOptions.map((o) => o.id).filter((id) => id && !isGeoAllSelectionToken(id))
          : (filters.region || []).filter((id) => !isGeoAllSelectionToken(id));
        if (regionCodes?.length) {
          const zoneSel = isGeoAllFilterValue(filters.zone)
            ? getUserScopedGeoCodesForDashboard(loginUser, "zone", userGeographyRef.current)
            : filters.zone;
          const countrySel = isGeoAllFilterValue(filters.country)
            ? getUserScopedGeoCodesForDashboard(loginUser, "country", userGeographyRef.current)
            : filters.country;
          loadTerritoryOptions(
            regionCodes,
            zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
            regionOptions.length ? regionOptions : regionOptionsRef.current,
            Array.isArray(zoneSel) && zoneSel.length ? zoneSel : filters.zone,
            Array.isArray(countrySel) && countrySel.length ? countrySel : filters.country
          );
        }
        return;
      }
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (!filters.region?.length) {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: [] }));
      return;
    }
    const regionsReady =
      regionOptions.length > 0 || regionOptionsRef.current.length > 0;
    if (!regionsReady) return;
    setTerritoryOptions([]);
    loadTerritoryOptions(
      filters.region,
      zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
      regionOptions.length ? regionOptions : regionOptionsRef.current,
      filters.zone,
      filters.country
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country, regionOptions, zoneOptions, scopeToUser]);

  useEffect(() => {
    if (!filterExpanded || !filterDateRangePickerRef.current) return;
    const $input = $(filterDateRangePickerRef.current);
    if (!$input.length) return;

    const initDateRangePicker = () => {
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }
      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
        showDropdowns: true,
        ranges: getDashboardDateRangePresets(),
      });
      $input.on("apply.daterangepicker", function (_ev, picker) {
        const displayText =
          picker.startDate.format("DD-MM-YYYY") +
          " - " +
          picker.endDate.format("DD-MM-YYYY");
        setFilters((prev) => ({
          ...prev,
          dateRange: displayText,
          dateRangeStart: picker.startDate.format("YYYY-MM-DD"),
          dateRangeEnd: picker.endDate.format("YYYY-MM-DD"),
        }));
        $(this).val(displayText);
      });
      $input.on("cancel.daterangepicker", function () {
        setFilters((prev) => ({ ...prev, dateRange: "" }));
        $(this).val("");
      });
      if (filters.dateRange) $input.val(filters.dateRange);
    };

    const timer = setTimeout(initDateRangePicker, 100);
    return () => {
      clearTimeout(timer);
      if ($input.data("daterangepicker")) {
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
        $input.data("daterangepicker").remove();
      }
    };
  }, [filterExpanded, filters.dateRange]);

  return {
    defaultFY,
    filters,
    setFilters,
    filterExpanded,
    setFilterExpanded,
    filterDateRangePickerRef,
    statusOptions,
    srCategoryOptions,
    bvOptions,
    marketOptions,
    countryOptions,
    zoneOptions,
    regionOptions,
    territoryOptions,
    geoLoading,
    isExportCascade,
    exportCountryOptions,
    isGeoAllFilterValue,
    handleGeoFilterChange,
    buildFilterParams,
    handleResetFilters,
    loadMarketOptions,
    marketDropdownDisabled,
    scopeToUser,
  };
}
